import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Clock,
  MapPin,
  Check,
  X,
  Calendar,
  ArrowRight,
  Phone,
  MessageCircle,
} from "lucide-react";
import axios from "axios";
import logger from '@/utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PackageDetailPage = () => {
  const { id } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackage = async () => {
      try {
        const response = await axios.get(`${API}/packages/${id}`);
        setPkg(response.data);
        // Track recently viewed in localStorage
        try {
          const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
          const pkgData = response.data;
          const entry = { id: pkgData.id || id, title: pkgData.title, image: pkgData.images?.[0], price: pkgData.price, destination: pkgData.destination, duration: pkgData.duration };
          const filtered = viewed.filter(v => v.id !== entry.id);
          filtered.unshift(entry);
          localStorage.setItem('recentlyViewed', JSON.stringify(filtered.slice(0, 10)));
        } catch (err) {
          logger.error('Failed to track recently viewed:', err);
        }
      } catch (e) {
        logger.error("Error fetching package:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchPackage();
  }, [id]);

  // Default itinerary only shown if no custom itinerary exists
  const defaultItinerary = [
    { day: 1, title: "Arrival & Welcome", description: "Airport pickup, hotel check-in, welcome dinner", activities: [] },
    { day: 2, title: "City Exploration", description: "Full day sightseeing of major attractions with guide", activities: [] },
    { day: 3, title: "Adventure Day", description: "Exciting activities and local experiences", activities: [] },
    { day: 4, title: "Cultural Immersion", description: "Visit to cultural sites, local markets, traditional shows", activities: [] },
    { day: 5, title: "Leisure & Departure", description: "Free time for shopping, airport transfer", activities: [] },
  ];

  // Default exclusions
  const defaultExclusions = ["Personal expenses", "Travel insurance", "Tips & gratuities", "Optional activities", "Visa fees (if applicable)"];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading package details...</div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-heading text-primary mb-4">Package Not Found</h2>
          <Link to="/packages">
            <Button>View All Packages</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Use package itinerary if available, otherwise use default
  const itinerary = pkg.itinerary && pkg.itinerary.length > 0 ? pkg.itinerary : defaultItinerary;
  const hasCustomItinerary = pkg.itinerary && pkg.itinerary.length > 0;
  
  // Use package exclusions if available, otherwise use default
  const exclusions = pkg.exclusions && pkg.exclusions.length > 0 ? pkg.exclusions : defaultExclusions;

  // Get types for display
  const types = pkg.types || (pkg.type ? [pkg.type] : []);

  return (
    <div data-testid="package-detail-page">
      {/* Hero */}
      <section className="relative pt-24 pb-16 bg-slate-900" data-testid="package-hero">
        <div className="absolute inset-0">
          <img
            src={pkg.image}
            alt={pkg.title}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-slate-900/50" />
        </div>
        <div className="container-custom relative z-10 pt-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-wrap gap-3 mb-4">
              <span className="px-4 py-1 bg-coral text-white text-sm font-medium rounded-full uppercase">
                {pkg.category}
              </span>
              {types.map((type) => (
                <span key={`type-${type}`} className="px-4 py-1 bg-white/20 text-white text-sm font-medium rounded-full uppercase">
                  {type}
                </span>
              ))}
            </div>
            <h1 className="heading-1 text-white mb-4">{pkg.title}</h1>
            <div className="flex flex-wrap items-center gap-6 text-white/80">
              <span className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                {pkg.destination}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {pkg.duration}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Available Year Round
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-white" data-testid="package-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Overview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="heading-3 text-primary mb-6">Package Overview</h2>
                <p className="body-lg">
                  Experience the best of {pkg.destination} with our carefully crafted {pkg.title} package. 
                  This {pkg.duration} journey takes you through the most iconic attractions and hidden gems, 
                  ensuring an unforgettable travel experience. Perfect for {types.join(', ')} travelers looking for 
                  a blend of adventure, culture, and relaxation.
                </p>
              </motion.div>

              {/* Highlights */}
              {pkg.highlights && pkg.highlights.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <h2 className="heading-3 text-primary mb-6">Tour Highlights</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {pkg.highlights.map((highlight, index) => (
                      <div
                        key={`highlight-${highlight.slice(0, 20)}-${index}`}
                        className="flex items-center gap-3 p-4 bg-accent rounded-lg"
                        data-testid={`highlight-${index}`}
                      >
                        <div className="w-8 h-8 bg-coral/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="h-4 w-4 text-coral" />
                        </div>
                        <span className="font-medium text-primary">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Itinerary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h2 className="heading-3 text-primary mb-6">
                  {hasCustomItinerary ? 'Day-wise Itinerary' : 'Sample Itinerary'}
                </h2>
                <div className="space-y-4">
                  {itinerary.map((day) => (
                    <div
                      key={`itin-day-${day.day}`}
                      className="p-5 bg-accent rounded-xl border border-slate-100"
                      data-testid={`itinerary-day-${day.day}`}
                    >
                      <div className="flex gap-4">
                        <div className="w-14 h-14 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                          <div className="text-center">
                            <span className="text-white text-xs block">Day</span>
                            <span className="text-white font-bold text-lg">{day.day}</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-primary text-lg mb-2">{day.title}</h4>
                          {day.description && (
                            <div 
                              className="text-slate-600 mb-3 prose prose-sm max-w-none
                                [&>ul]:list-disc [&>ul]:ml-4 [&>ul]:my-2
                                [&>ol]:list-decimal [&>ol]:ml-4 [&>ol]:my-2
                                [&>li]:my-1
                                [&_b]:font-bold [&_strong]:font-bold
                                [&_i]:italic [&_em]:italic"
                              dangerouslySetInnerHTML={{ 
                                __html: DOMPurify.sanitize(day.description
                                  .replace(/\n/g, '<br/>')
                                  .replace(/•\s*/g, '<br/>• ')
                                  .replace(/^<br\/>/, ''))
                              }}
                            />
                          )}
                          {day.activities && day.activities.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {day.activities.map((activity, actIdx) => (
                                <span
                                  key={actIdx}
                                  className="px-3 py-1 bg-white rounded-full text-sm text-slate-700 border border-slate-200"
                                >
                                  {activity}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {!hasCustomItinerary && (
                  <p className="mt-4 text-sm text-slate-500 italic">
                    * This is a sample itinerary. Final itinerary may vary based on availability and your preferences.
                  </p>
                )}
              </motion.div>

              {/* Inclusions / Exclusions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="grid sm:grid-cols-2 gap-8"
              >
                <div>
                  <h3 className="heading-3 text-primary mb-4">Inclusions</h3>
                  <ul className="space-y-3">
                    {pkg.inclusions?.map((item, index) => (
                      <li key={`inc-${item.slice(0, 20)}-${index}`} className="flex items-center gap-3 text-slate-600" data-testid={`inclusion-${index}`}>
                        <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                    <li className="flex items-center gap-3 text-slate-600">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      Sightseeing as per itinerary
                    </li>
                    <li className="flex items-center gap-3 text-slate-600">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      All applicable taxes
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="heading-3 text-primary mb-4">Exclusions</h3>
                  <ul className="space-y-3">
                    {exclusions.map((item, index) => (
                      <li key={`exc-${item.slice(0, 20)}-${index}`} className="flex items-center gap-3 text-slate-600" data-testid={`exclusion-${index}`}>
                        <X className="h-5 w-5 text-red-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              {/* Terms & Conditions */}
              {pkg.terms_conditions && pkg.terms_conditions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <h3 className="heading-3 text-primary mb-4">Terms & Conditions</h3>
                  <ul className="space-y-2 text-slate-600 text-sm">
                    {pkg.terms_conditions
                      .filter(term => term && term.trim() && !term.toLowerCase().includes('trip terms & conditions'))
                      .map((term, index) => (
                        <li key={`term-${term.slice(0, 20)}-${index}`} className="flex items-start gap-2">
                          <span className="text-coral font-bold">•</span>
                          {term.replace(/^[•\-\*]\s*/, '').trim()}
                        </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28">
                <Card className="shadow-xl" data-testid="booking-card">
                  <CardHeader className="bg-primary text-white rounded-t-xl">
                    <CardTitle className="text-center">
                      <span className="text-sm font-normal block mb-1">Starting from</span>
                      <span className="text-4xl font-bold">₹{pkg.price?.toLocaleString()}</span>
                      <span className="text-sm font-normal block mt-1">per person</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Duration</span>
                        <span className="font-medium">{pkg.duration}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Package Code</span>
                        <span className="font-mono font-medium text-coral">#{pkg.package_code}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Package Type</span>
                        <span className="font-medium capitalize">{types.join(', ')}</span>
                      </div>
                    </div>

                    <Link to={`/get-quote?package=${pkg.title}&destination=${pkg.destination}`} className="block">
                      <Button className="btn-coral w-full" data-testid="enquire-now-btn">
                        Enquire Now
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>

                    <div className="flex gap-3">
                      <a href="tel:+918369033791" className="flex-1">
                        <Button variant="outline" className="w-full" data-testid="call-btn">
                          <Phone className="mr-2 h-4 w-4" />
                          Call
                        </Button>
                      </a>
                      <a
                        href={`https://wa.me/918369033791?text=Hi, I'm interested in the ${pkg.title} package (Code: ${pkg.package_code})`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button variant="outline" className="w-full border-green-500 text-green-600 hover:bg-green-50" data-testid="whatsapp-btn">
                          <MessageCircle className="mr-2 h-4 w-4" />
                          WhatsApp
                        </Button>
                      </a>
                    </div>

                    <p className="text-xs text-center text-slate-500">
                      Free consultation • No booking fees
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PackageDetailPage;
